import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import {
  addMaintenance,
  addMileage,
  cancelRecord,
  correctRecord,
  deleteMaintenance,
  fetchHistory,
  fetchMileage,
  fetchVehicle,
  maintenanceLabel,
  registerMaintenance,
  updateMaintenance,
  updateVehicle,
  MAINTENANCE_TYPE_LABELS,
  type Maintenance,
  type MaintenanceInput,
  type MaintenanceRecord,
  type MaintenanceType,
  type MileageLog,
  type VehicleDetail,
} from "@/lib/adminFleetApi";
import { formatBrDate, formatKm, todayIso } from "@/lib/fleetFormat";
import { FleetAlertBadge } from "@/components/admin/FleetAlertBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, CheckCircle2, Gauge, History, Loader2, Pencil, Plus, Trash2, Wrench, XCircle,
} from "lucide-react";

const MAINTENANCE_TYPES = Object.keys(MAINTENANCE_TYPE_LABELS) as MaintenanceType[];

interface MaintDraft {
  type: MaintenanceType;
  customName: string;
  lastServiceDate: string;
  lastServiceMileage: string;
  intervalKm: string;
  intervalMonths: string;
  notes: string;
}

const EMPTY_MAINT: MaintDraft = {
  type: "OLEO_MOTOR", customName: "", lastServiceDate: "", lastServiceMileage: "",
  intervalKm: "", intervalMonths: "", notes: "",
};

const numOrNull = (v: string): number | null => (v.trim() ? Number(v) : null);

export default function AdminVehicleDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const canEditVeh = hasPermission(account, "FROTA.VEICULO.EDITAR");
  const canRegisterKm = hasPermission(account, "FROTA.QUILOMETRAGEM.REGISTRAR");
  const canCreateMaint = hasPermission(account, "FROTA.MANUTENCAO.CRIAR");
  const canRegisterMaint = hasPermission(account, "FROTA.MANUTENCAO.REGISTRAR");
  const canEditMaint = hasPermission(account, "FROTA.MANUTENCAO.EDITAR");
  const canDeleteMaint = hasPermission(account, "FROTA.MANUTENCAO.EXCLUIR");
  const canEditRegistro = hasPermission(account, "FROTA.REGISTRO.EDITAR");
  const canCancelRegistro = hasPermission(account, "FROTA.REGISTRO.CANCELAR");

  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null);
  const [mileageLogs, setMileageLogs] = useState<MileageLog[]>([]);
  const [history, setHistory] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Maintenance editor
  const [maintOpen, setMaintOpen] = useState(false);
  const [editingMaint, setEditingMaint] = useState<Maintenance | null>(null);
  const [maintDraft, setMaintDraft] = useState<MaintDraft>(EMPTY_MAINT);

  // Register realized maintenance
  const [registerFor, setRegisterFor] = useState<Maintenance | null>(null);
  const [regDate, setRegDate] = useState(todayIso());
  const [regMileage, setRegMileage] = useState("");
  const [regDesc, setRegDesc] = useState("");

  // Mileage
  const [mileageOpen, setMileageOpen] = useState(false);
  const [mDate, setMDate] = useState(todayIso());
  const [mValue, setMValue] = useState("");
  const [mNotes, setMNotes] = useState("");

  // Record correction / cancellation
  const [correctFor, setCorrectFor] = useState<MaintenanceRecord | null>(null);
  const [corrDate, setCorrDate] = useState("");
  const [corrMileage, setCorrMileage] = useState("");
  const [corrDesc, setCorrDesc] = useState("");
  const [cancelFor, setCancelFor] = useState<MaintenanceRecord | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, m, h] = await Promise.all([fetchVehicle(id), fetchMileage(id), fetchHistory(id)]);
      setVehicle(v);
      setMileageLogs(m);
      setHistory(h);
    } catch {
      toast.error("Erro ao carregar veículo");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  // ---- Maintenance ------------------------------------------------------------------------------

  const openAddMaint = () => { setEditingMaint(null); setMaintDraft(EMPTY_MAINT); setMaintOpen(true); };
  const openEditMaint = (m: Maintenance) => {
    setEditingMaint(m);
    setMaintDraft({
      type: m.type,
      customName: m.customName ?? "",
      lastServiceDate: m.lastServiceDate ?? "",
      lastServiceMileage: m.lastServiceMileage != null ? String(m.lastServiceMileage) : "",
      intervalKm: m.intervalKm != null ? String(m.intervalKm) : "",
      intervalMonths: m.intervalMonths != null ? String(m.intervalMonths) : "",
      notes: m.notes ?? "",
    });
    setMaintOpen(true);
  };

  const saveMaint = async () => {
    if (maintDraft.type === "OUTROS" && !maintDraft.customName.trim()) {
      toast.error("Informe o nome do serviço para o tipo \"Outros\""); return;
    }
    setBusy(true);
    try {
      const payload: MaintenanceInput = {
        type: maintDraft.type,
        customName: maintDraft.customName.trim() || undefined,
        lastServiceDate: maintDraft.lastServiceDate || null,
        lastServiceMileage: numOrNull(maintDraft.lastServiceMileage),
        intervalKm: numOrNull(maintDraft.intervalKm),
        intervalMonths: numOrNull(maintDraft.intervalMonths),
        notes: maintDraft.notes.trim() || undefined,
      };
      const updated = editingMaint
        ? await updateMaintenance(id, editingMaint.id, payload)
        : await addMaintenance(id, payload);
      setVehicle(updated);
      toast.success(editingMaint ? "Manutenção atualizada" : "Manutenção adicionada");
      setMaintOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar manutenção");
    } finally {
      setBusy(false);
    }
  };

  const removeMaint = async (m: Maintenance) => {
    if (!window.confirm(`Remover a manutenção "${maintenanceLabel(m.type, m.customName)}" do plano? O histórico é mantido.`)) return;
    try {
      await deleteMaintenance(id, m.id);
      setVehicle((v) => v ? { ...v, maintenances: v.maintenances.filter((x) => x.id !== m.id) } : v);
      toast.success("Manutenção removida do plano");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    }
  };

  const openRegister = (m: Maintenance) => {
    setRegisterFor(m);
    setRegDate(todayIso());
    setRegMileage(vehicle ? String(vehicle.currentMileage) : "");
    setRegDesc("");
  };

  const submitRegister = async () => {
    if (!registerFor) return;
    if (!regMileage.trim()) { toast.error("Informe a quilometragem"); return; }
    setBusy(true);
    try {
      const updated = await registerMaintenance(id, registerFor.id, {
        serviceDate: regDate,
        mileage: Number(regMileage),
        description: regDesc.trim() || undefined,
      });
      setVehicle(updated);
      setHistory(await fetchHistory(id));
      toast.success("Manutenção registrada");
      setRegisterFor(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar manutenção");
    } finally {
      setBusy(false);
    }
  };

  // ---- Mileage ----------------------------------------------------------------------------------

  const submitMileage = async () => {
    if (!mValue.trim()) { toast.error("Informe a quilometragem"); return; }
    setBusy(true);
    try {
      await addMileage(id, { logDate: mDate, mileage: Number(mValue), notes: mNotes.trim() || undefined });
      toast.success("Quilometragem registrada");
      setMileageOpen(false);
      setMValue(""); setMNotes(""); setMDate(todayIso());
      await load(); // recalcula alertas
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar quilometragem");
    } finally {
      setBusy(false);
    }
  };

  // ---- Records ----------------------------------------------------------------------------------

  const openCorrect = (r: MaintenanceRecord) => {
    setCorrectFor(r);
    setCorrDate(r.serviceDate);
    setCorrMileage(String(r.mileage));
    setCorrDesc(r.description ?? "");
  };

  const submitCorrect = async () => {
    if (!correctFor) return;
    setBusy(true);
    try {
      await correctRecord(id, correctFor.id, {
        serviceDate: corrDate,
        mileage: Number(corrMileage),
        description: corrDesc.trim() || undefined,
      });
      setHistory(await fetchHistory(id));
      toast.success("Registro corrigido");
      setCorrectFor(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao corrigir");
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelFor) return;
    if (!cancelReason.trim()) { toast.error("Informe a justificativa"); return; }
    setBusy(true);
    try {
      await cancelRecord(id, cancelFor.id, cancelReason.trim());
      setHistory(await fetchHistory(id));
      toast.success("Registro cancelado");
      setCancelFor(null);
      setCancelReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao cancelar");
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async () => {
    if (!vehicle) return;
    const next = vehicle.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      const updated = await updateVehicle(id, {
        plate: vehicle.plate,
        brand: vehicle.brand ?? undefined,
        model: vehicle.model ?? undefined,
        year: vehicle.year ?? null,
        status: next,
      });
      setVehicle(updated);
      toast.success(next === "ACTIVE" ? "Veículo reativado" : "Veículo inativado");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao alterar situação");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!vehicle) {
    return (
      <div className="container py-16 text-center text-muted-foreground">
        Veículo não encontrado.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate("/admin/frota")}>Voltar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container flex flex-col gap-6 py-6">
      <div>
        <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate("/admin/frota")}>
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
      </div>

      {/* Header */}
      <div className="rounded-2xl border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{vehicle.plate}</h1>
              <FleetAlertBadge status={vehicle.alertStatus} />
              {vehicle.status === "INACTIVE" && <Badge variant="secondary">Inativo</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">
              {[vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "Sem marca/modelo"}
              {vehicle.year ? ` · ${vehicle.year}` : ""} · {formatKm(vehicle.currentMileage)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canRegisterKm && (
              <Button variant="outline" className="gap-2" onClick={() => { setMDate(todayIso()); setMileageOpen(true); }}>
                <Gauge className="h-4 w-4" /> Registrar km
              </Button>
            )}
            {canEditVeh && (
              <Button variant="outline" onClick={() => void toggleStatus()}>
                {vehicle.status === "ACTIVE" ? "Inativar" : "Reativar"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="maintenances">
        <TabsList>
          <TabsTrigger value="maintenances" className="gap-1"><Wrench className="h-4 w-4" /> Manutenções</TabsTrigger>
          <TabsTrigger value="mileage" className="gap-1"><Gauge className="h-4 w-4" /> Quilometragem</TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><History className="h-4 w-4" /> Histórico</TabsTrigger>
        </TabsList>

        {/* Manutenções */}
        <TabsContent value="maintenances" className="mt-4 flex flex-col gap-3">
          <div className="flex justify-end">
            {canCreateMaint && (
              <Button size="sm" className="gap-2" onClick={openAddMaint}><Plus className="h-4 w-4" /> Adicionar manutenção</Button>
            )}
          </div>
          {vehicle.maintenances.length === 0 ? (
            <EmptyState icon={Wrench} text="Nenhuma manutenção cadastrada para este veículo" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {vehicle.maintenances.map((m) => (
                <div key={m.id} className="rounded-xl border bg-card p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{maintenanceLabel(m.type, m.customName)}</div>
                    <FleetAlertBadge status={m.alertStatus} />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <Info label="Última troca" value={formatBrDate(m.lastServiceDate)} />
                    <Info label="Km da última" value={m.lastServiceMileage != null ? formatKm(m.lastServiceMileage) : "—"} />
                    <Info label="Intervalo" value={m.intervalMonths != null ? `${m.intervalMonths} mês(es)` : "—"} />
                    <Info label="Intervalo km" value={m.intervalKm != null ? formatKm(m.intervalKm) : "—"} />
                    <Info label="Próxima data" value={formatBrDate(m.nextServiceDate)} />
                    <Info label="Próxima km" value={m.nextServiceMileage != null ? formatKm(m.nextServiceMileage) : "—"} />
                  </dl>
                  {m.notes && <p className="mt-2 text-xs text-muted-foreground">{m.notes}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {canRegisterMaint && (
                      <Button size="sm" className="gap-1" onClick={() => openRegister(m)}>
                        <CheckCircle2 className="h-4 w-4" /> Registrar realização
                      </Button>
                    )}
                    {canEditMaint && (
                      <Button size="sm" variant="ghost" onClick={() => openEditMaint(m)}><Pencil className="h-4 w-4" /></Button>
                    )}
                    {canDeleteMaint && (
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => void removeMaint(m)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Quilometragem */}
        <TabsContent value="mileage" className="mt-4 flex flex-col gap-3">
          <div className="flex justify-end">
            {canRegisterKm && (
              <Button size="sm" className="gap-2" onClick={() => { setMDate(todayIso()); setMileageOpen(true); }}>
                <Plus className="h-4 w-4" /> Registrar km
              </Button>
            )}
          </div>
          {mileageLogs.length === 0 ? (
            <EmptyState icon={Gauge} text="Nenhum registro de quilometragem" />
          ) : (
            <div className="rounded-xl border bg-card shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 font-medium">Data</th>
                    <th className="px-4 py-2 font-medium">Km</th>
                    <th className="px-4 py-2 font-medium">Responsável</th>
                    <th className="px-4 py-2 font-medium">Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {mileageLogs.map((l) => (
                    <tr key={l.id} className="border-b last:border-0">
                      <td className="px-4 py-2">{formatBrDate(l.logDate)}</td>
                      <td className="px-4 py-2">{formatKm(l.mileage)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{l.recordedBy ?? "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{l.notes ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Histórico */}
        <TabsContent value="history" className="mt-4 flex flex-col gap-3">
          {history.length === 0 ? (
            <EmptyState icon={History} text="Nenhuma manutenção realizada registrada" />
          ) : (
            <div className="flex flex-col gap-3">
              {history.map((r) => (
                <div key={r.id} className={`rounded-xl border bg-card p-4 shadow-sm ${r.cancelled ? "opacity-60" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">
                        {maintenanceLabel(r.type, r.customName)}
                        {r.cancelled && <Badge variant="secondary" className="ml-2">Cancelado</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatBrDate(r.serviceDate)} · {formatKm(r.mileage)} · por {r.recordedBy ?? "—"}
                      </div>
                    </div>
                    {!r.cancelled && (canEditRegistro || canCancelRegistro) && (
                      <div className="flex gap-1">
                        {canEditRegistro && (
                          <Button size="sm" variant="ghost" onClick={() => openCorrect(r)}><Pencil className="h-4 w-4" /></Button>
                        )}
                        {canCancelRegistro && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setCancelFor(r)}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {r.description && <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>}
                  {r.cancelled && r.cancellationReason && (
                    <p className="mt-2 text-xs text-destructive">Motivo do cancelamento: {r.cancellationReason}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog: manutenção (add/edit) */}
      <Dialog open={maintOpen} onOpenChange={(o) => { if (!o) setMaintOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingMaint ? "Editar manutenção" : "Nova manutenção"}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Tipo de manutenção</Label>
              <Select value={maintDraft.type} onValueChange={(v) => setMaintDraft((d) => ({ ...d, type: v as MaintenanceType }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MAINTENANCE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{MAINTENANCE_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {maintDraft.type === "OUTROS" && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-custom">Nome do serviço *</Label>
                <Input id="m-custom" value={maintDraft.customName} onChange={(e) => setMaintDraft((d) => ({ ...d, customName: e.target.value }))} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-date">Última troca/revisão</Label>
                <Input id="m-date" type="date" value={maintDraft.lastServiceDate} onChange={(e) => setMaintDraft((d) => ({ ...d, lastServiceDate: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-km">Km da última</Label>
                <Input id="m-km" inputMode="numeric" value={maintDraft.lastServiceMileage} onChange={(e) => setMaintDraft((d) => ({ ...d, lastServiceMileage: e.target.value.replace(/\D/g, "") }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-ik">Intervalo (km)</Label>
                <Input id="m-ik" inputMode="numeric" value={maintDraft.intervalKm} onChange={(e) => setMaintDraft((d) => ({ ...d, intervalKm: e.target.value.replace(/\D/g, "") }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-im">Intervalo (meses)</Label>
                <Input id="m-im" inputMode="numeric" value={maintDraft.intervalMonths} onChange={(e) => setMaintDraft((d) => ({ ...d, intervalMonths: e.target.value.replace(/\D/g, "") }))} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="m-notes">Observações</Label>
              <Textarea id="m-notes" rows={2} value={maintDraft.notes} onChange={(e) => setMaintDraft((d) => ({ ...d, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMaintOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={() => void saveMaint()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingMaint ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: registrar realização */}
      <Dialog open={!!registerFor} onOpenChange={(o) => { if (!o) setRegisterFor(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registrar manutenção realizada</DialogTitle></DialogHeader>
          {registerFor && (
            <p className="text-sm text-muted-foreground">{maintenanceLabel(registerFor.type, registerFor.customName)}</p>
          )}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="r-date">Data da realização</Label>
              <Input id="r-date" type="date" value={regDate} onChange={(e) => setRegDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="r-km">Quilometragem</Label>
              <Input id="r-km" inputMode="numeric" value={regMileage} onChange={(e) => setRegMileage(e.target.value.replace(/\D/g, ""))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="r-desc">Descrição / observação</Label>
              <Textarea id="r-desc" rows={2} value={regDesc} onChange={(e) => setRegDesc(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRegisterFor(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={() => void submitRegister()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: registrar km */}
      <Dialog open={mileageOpen} onOpenChange={(o) => { if (!o) setMileageOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registrar quilometragem</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Atual: {formatKm(vehicle.currentMileage)}. Não é permitido registrar valor inferior.</p>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mk-date">Data</Label>
              <Input id="mk-date" type="date" value={mDate} onChange={(e) => setMDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mk-km">Quilometragem</Label>
              <Input id="mk-km" inputMode="numeric" value={mValue} onChange={(e) => setMValue(e.target.value.replace(/\D/g, ""))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mk-notes">Observações</Label>
              <Textarea id="mk-notes" rows={2} value={mNotes} onChange={(e) => setMNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMileageOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={() => void submitMileage()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: corrigir registro */}
      <Dialog open={!!correctFor} onOpenChange={(o) => { if (!o) setCorrectFor(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Corrigir registro</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cc-date">Data</Label>
              <Input id="cc-date" type="date" value={corrDate} onChange={(e) => setCorrDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cc-km">Quilometragem</Label>
              <Input id="cc-km" inputMode="numeric" value={corrMileage} onChange={(e) => setCorrMileage(e.target.value.replace(/\D/g, ""))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cc-desc">Descrição</Label>
              <Textarea id="cc-desc" rows={2} value={corrDesc} onChange={(e) => setCorrDesc(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectFor(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={() => void submitCorrect()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar correção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: cancelar registro */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => { if (!o) { setCancelFor(null); setCancelReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cancelar registro</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">O registro é mantido no histórico. Informe a justificativa.</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cx-reason">Justificativa *</Label>
            <Textarea id="cx-reason" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setCancelFor(null); setCancelReason(""); }} disabled={busy}>Voltar</Button>
            <Button variant="destructive" onClick={() => void submitCancel()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Cancelar registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide opacity-70">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: React.ComponentType<{ className?: string }>; text: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-14 text-muted-foreground">
      <Icon className="h-10 w-10 opacity-30" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
